import { type PropsWithChildren, type ReactElement } from 'react'
import { Box, Toolbar } from '@dhl-ui/components'
import { Content, ContentRow } from '../components'
import { SubPageToolbar } from './SubPageToolbar'

export type SubPageProps = PropsWithChildren<{
  /**
   * Short title telling our user which channel they went into :)
   */
  title: string

  /**
   * You can have influence on the header its background
   * @default 'none'
   */
  headerBackground?: 'none' | 'subtle'

  /**
   * For testing purposes
   */
  'data-testid'?: string

  /**
   * We have a difference in behaviour: back and/or close
   * Choose onBack when going to a previous step
   */
  onBack?: () => void

  /**
   * We have a difference in behaviour: back and/or close
   * Choose onClose when stopping/aborting the flow
   */
  onClose?: () => void

  /**
   * Its possible to have more options in our right section, pleace make sure to give these icon buttons an edge and use size small!
   */
  endNodes?: ReactElement
}>

/**
 * This is our master SubPage only meant for our **mobile** variant.
 * In some cases you need pull to refresh, then please use `SubPageWithRefresh`.
 *
 * We also exported our used Toolbar as `SubPageToolbar` which can be usefull when composing your own SubPage (for example when you use a plain Modal over your main Page as SubPage for not loosing main state nore painting it again)
 */
const SubPage = ({ children, 'data-testid': testid, ...rest }: SubPageProps) => (
  <Box
    data-testid={testid}
    sx={{
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        zIndex: ({ zIndex }) => zIndex.drawer,
        position: 'fixed',
        top: 0,
        right: 0,
        left: 0,
        boxSizing: 'border-box',
        borderBottom: rest.headerBackground === 'subtle' ? '1px solid transparent' : undefined,
        borderBottomColor: ({ palette }) => (rest.headerBackground === 'subtle' ? palette.divider : undefined),
      }}
    >
      <SubPageToolbar {...rest} />
    </Box>

    <Box
      sx={{
        flex: 1,
      }}
      data-testid="sub-page-content"
    >
      <Toolbar />
      {children}
    </Box>
  </Box>
)

/**
 * A container where we set the content distance based on the theme correctly.
 *
 * @example <SubPage><SubPage.Content isFullView>your application content</SubPage.Content></SubPage>
 */
SubPage.Content = Content

/**
 * Our mobile version is a full wide screen, this ContentRow can be used when
 * it does need indentation for a specific row. This indentation will only be
 * added on mobile variants.
 * @warning will add indentation only on MOBILE
 */
SubPage.ContentRow = ContentRow

export { SubPage }
